import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { DataDialog } from "@app/components/atoms";
import { IconDelete } from "@app/components/icons";
import { MaintenanceReportIcon } from "@app/components/icons/ReportIcons/ReportIcons";
import { useAppContext } from "@app/AppContext";
import { setShowAlert } from "@app/store";
import { MaintenanceReportType } from "@app/constants";
import {
  apiDeleteMaintenanceReportDraft,
  apiGetMaintenanceReportDraft,
  apiGetMaintenanceReportDraftList,
} from "@app/api/machines/machines.api";
import { CreateMaintenance } from "./CreateMaintenance";

type MaintenanceDraftSummary = {
  reportedId: string;
  reportedAt: string;
  reportItem: string;
  isPeriodicMaintenance?: boolean;
};

const ROW_ICON_SLOT_SIZE = 24;

function DraftCardSkeleton() {
  return (
    <div className="report-card">
      <div className="content-report">
        <div className="header flex align-items-center">
          <Skeleton
            variant="circular"
            width={32}
            height={32}
            sx={{ flexShrink: 0 }}
          />
          <div className="report-detail" style={{ flex: 1 }}>
            <Skeleton variant="text" width="100%" height={20} />
            <Skeleton variant="text" width="100%" height={20} />
            <Skeleton variant="text" width="40%" height={16} sx={{ mt: 0.5 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

const TITLE_CLAMP_LINES = 2;

function ClampedMaintenanceTitle({ text }: { text: string }) {
  const ref = useRef<HTMLHeadingElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setIsTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  return (
    <Tooltip
      title={text}
      disableHoverListener={!isTruncated}
      disableTouchListener={!isTruncated}
      enterTouchDelay={100}
    >
      <h3
        ref={ref}
        className="report-name txt-black-1 word-break"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: TITLE_CLAMP_LINES,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {text}
      </h3>
    </Tooltip>
  );
}

type Props = {
  open: boolean;
  onClose: () => void;
  groupId: string;
  machineId: string;
  onDraftPublished?: () => void;
};

export function MaintenanceDraftsListDialog({
  open,
  onClose,
  groupId,
  machineId,
  onDraftPublished,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { userTimeFormat } = useAppContext();

  const [isLoading, setIsLoading] = useState(false);
  const [drafts, setDrafts] = useState<MaintenanceDraftSummary[]>([]);

  const [openEditingDraft, setOpenEditingDraft] = useState(false);
  const [editingDraftReport, setEditingDraftReport] = useState<any>(null);
  const [isLoadingDraftDetail, setIsLoadingDraftDetail] = useState<
    string | null
  >(null);

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchDrafts = useCallback(() => {
    if (!groupId || !machineId) {
      return Promise.resolve<MaintenanceDraftSummary[]>([]);
    }
    setIsLoading(true);
    return apiGetMaintenanceReportDraftList(groupId, machineId)
      .then((res) => {
        const reversed = [...(res?.data?.data ?? [])].reverse();
        setDrafts(reversed);
        return reversed;
      })
      .catch((error) => {
        console.error("Failed to load maintenance report drafts:", error);
        setDrafts([]);
        return [];
      })
      .finally(() => setIsLoading(false));
  }, [groupId, machineId]);

  useEffect(() => {
    if (open) {
      fetchDrafts();
    }
  }, [open, fetchDrafts]);

  const handleOpenDraft = (machineReportId: string) => {
    if (!groupId || !machineId) return;
    setIsLoadingDraftDetail(machineReportId);
    apiGetMaintenanceReportDraft(groupId, machineId, machineReportId)
      .then((res) => {
        const draftReport = res?.data?.data;
        if (!draftReport) return;
        setEditingDraftReport(draftReport);
        setOpenEditingDraft(true);
      })
      .catch((error) => {
        console.error("Failed to load maintenance report draft:", error);
      })
      .finally(() => setIsLoadingDraftDetail(null));
  };

  const handleCloseEditingDraft = () => {
    setOpenEditingDraft(false);
    setEditingDraftReport(null);
  };

  const handleDraftUpdateSuccess = () => {
    fetchDrafts().then((remainingDrafts) => {
      if (remainingDrafts.length === 0) {
        onClose();
      }
    });
    onDraftPublished?.();
  };

  const handleConfirmDelete = () => {
    if (!deleteTargetId || !groupId || !machineId) return;
    setIsDeleting(true);
    apiDeleteMaintenanceReportDraft(groupId, machineId, deleteTargetId)
      .then((res) => {
        dispatch(setShowAlert(res?.data?.meta?.successMessage || ""));
        setDeleteTargetId(null);
        fetchDrafts().then((remainingDrafts) => {
          if (remainingDrafts.length === 0) {
            onClose();
          }
        });
        onDraftPublished?.();
      })
      .catch((error) => {
        console.error("Failed to delete maintenance report draft:", error);
      })
      .finally(() => setIsDeleting(false));
  };

  return (
    <>
      <DataDialog
        title={t("draftReports")}
        open={open}
        onClose={onClose}
        disableBackdropClick={false}
        className="dialog-report dialog-maintenance-drafts"
        dialogActions={
          <Button onClick={onClose} className="btn btn-no-border">
            {t("dialogRefuel.close")}
          </Button>
        }
      >
        <div
          className="content"
          style={{ minWidth: 320, minHeight: "52vh" }}
        >
          {isLoading && (
            <>
              <DraftCardSkeleton />
              <DraftCardSkeleton />
              <DraftCardSkeleton />
            </>
          )}

          {!isLoading && drafts.length === 0 && (
            <p className="txt-gray-1 fs-16">{t("noDraftReports")}</p>
          )}

          {!isLoading &&
            drafts.map((draftItem) => (
              <div key={draftItem.reportedId} className="report-card">
                <div
                  className="content-report pointer"
                  onClick={() => handleOpenDraft(draftItem.reportedId)}
                >
                  <div className="header flex align-items-center justify-content-between">
                    <div className="flex align-items-center" style={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          width: ROW_ICON_SLOT_SIZE,
                          height: ROW_ICON_SLOT_SIZE,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {isLoadingDraftDetail === draftItem.reportedId ? (
                          <CircularProgress size={20} sx={{ color: "#140a9a" }} />
                        ) : (
                          <MaintenanceReportIcon />
                        )}
                      </Box>
                      <div className="report-detail">
                        <ClampedMaintenanceTitle
                          text={draftItem.reportItem || t("maintenanceReport")}
                        />
                        <p className="report-time txt-black-1">
                          {draftItem.isPeriodicMaintenance
                            ? t("dialogMaintenanceReports.isPeriodicMaintenance")
                            : t("generalMaintenance")}
                          {draftItem.reportedAt &&
                          !isNaN(new Date(draftItem.reportedAt).getTime())
                            ? ` · ${format(
                                new Date(draftItem.reportedAt),
                                userTimeFormat,
                              )}`
                            : ""}
                        </p>
                      </div>
                    </div>
                    <IconButton
                      size="small"
                      disableRipple
                      onClick={(event) => {
                        event.stopPropagation();
                        setDeleteTargetId(draftItem.reportedId);
                      }}
                      aria-label={t("formAction.delete")}
                      sx={{
                        "&:focus, &:focus-visible": { outline: "none" },
                      }}
                    >
                      <IconDelete />
                    </IconButton>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </DataDialog>

      <CreateMaintenance
        open={openEditingDraft}
        onClose={handleCloseEditingDraft}
        groupId={groupId}
        machineId={machineId}
        maintenanceType={
          editingDraftReport?.isPeriodicMaintenance
            ? MaintenanceReportType.PERIODIC_MAINTENANCE
            : MaintenanceReportType.GENERAL_MAINTENANCE
        }
        mode="draft"
        editingReport={editingDraftReport}
        onUpdateSuccess={handleDraftUpdateSuccess}
      />

      <DataDialog
        title={t("titleDeleteCard")}
        open={!!deleteTargetId}
        setOpen={(isOpen: boolean) => !isOpen && setDeleteTargetId(null)}
        disableBackdropClick={true}
      >
        <div className="form-confirm-delete">
          <p className="txt-gray-1 fs-16">{t("desDeleteCard")}</p>
          <div className="button-form-delete flex justify-content-end">
            <Button
              onClick={() => setDeleteTargetId(null)}
              className="btn btn-no-border"
              disabled={isDeleting}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              className="btn-delete"
              disabled={isDeleting}
            >
              {t("formAction.delete")}
            </Button>
          </div>
        </div>
      </DataDialog>
    </>
  );
}
